// CSV/JSON import-export and PDF print helpers

export const toCSV = (rows, columns) => {
  // columns: [{ key, header }]
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    let s = String(v);
    // Prevent CSV formula injection: a leading = + - @ (or tab/CR) makes Excel/Sheets
    // treat the cell as a formula. Prefix with ' so it stays plain text.
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    s = s.replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const head = columns.map(c => escape(c.header)).join(',');
  const body = rows.map(r => columns.map(c => escape(r[c.key])).join(',')).join('\n');
  return head + '\n' + body;
};

export const downloadFile = (filename, content, type = 'text/csv') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

// A cell is allowed to contain a line break when it is quoted, and GorillaDesk
// writes addresses that way: "5730 Lakewood Boulevard\nLakewood CA 90712". Reading
// the file line by line and splitting each line therefore tore those rows in half
// and lost them: helen's 710-job export came out as 828 rows of which only 593
// still had a readable date. So this walks the text one character at a time and
// only treats a line break as the end of a row when it falls outside quotes.
const parseRows = (text) => {
  const rows = [];
  let row = [];
  let cur = '';
  let quoted = false;
  // A trailing newline would otherwise produce one empty row at the end.
  const src = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') {
      quoted = true;
    } else if (c === ',') {
      row.push(cur); cur = '';
    } else if (c === '\n') {
      row.push(cur); cur = '';
      rows.push(row); row = [];
    } else {
      cur += c;
    }
  }
  row.push(cur);
  rows.push(row);
  // A row of nothing but empty cells is blank padding, not a record.
  return rows.filter(r => r.some(cell => String(cell).trim() !== ''));
};

export const parseCSV = (text) => {
  const rows = parseRows(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(h => String(h).trim());
  return rows.slice(1).map(cells => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] ?? ''; });
    return obj;
  });
};

/**
 * A row's value for the first of `names` that matches one of its headers,
 * ignoring case, spaces and underscores.
 *
 * CSV headers come from whatever produced the file, so a column can arrive as
 * "Date", "date", "Service Date" or "service_date" and all of them have to find
 * the same column. Reading `row.Date` directly finds only the one spelling, and
 * a caller that then falls back to a default cannot tell a blank cell from a
 * column it simply failed to look up.
 */
export const pickCell = (row, ...names) => {
  const norm = (s) => String(s).toLowerCase().replace(/[\s_]+/g, '');
  const cells = Object.entries(row || {}).map(([k, v]) => [norm(k), v]);
  for (const name of names) {
    const hit = cells.find(([k]) => k === norm(name));
    if (hit && String(hit[1] ?? '').trim()) return String(hit[1]).trim();
  }
  return '';
};

export const exportJSON = (filename, data) => {
  downloadFile(filename, JSON.stringify(data, null, 2), 'application/json');
};

export const printElement = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return;
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>Print</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 32px; color: #0f172a; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border-bottom: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; font-size: 13px; }
    th { background: #f8fafc; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; }
    h1, h2, h3 { color: #8BC53F; }
    .brand { color: #8BC53F; font-weight: 800; }
    .right { text-align: right; }
  </style></head><body>${el.innerHTML}</body></html>`);
  w.document.close();
  setTimeout(() => { w.print(); }, 350);
};

export const readFileAsText = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsText(file);
});

// Self-contained print helper used by the July-line pages (Collections, Pest
// Library, Service Instructions). Opens a print window and renders the HTML.
export const printHTML = (innerHTML, title = 'Xpertpests Solutions') => {
  const w = window.open('', '_blank', 'width=900,height=650');
  if (!w) { alert('Please allow pop-ups to preview or print documents.'); return; }
  w.document.write(
    '<!doctype html><html><head><title>' + title + '</title>' +
    '<meta charset="utf-8"><style>' +
    'body{font-family:Arial,Helvetica,sans-serif;color:#14201a;padding:24px;line-height:1.5}' +
    'h1,h2,h3{color:#0d2818} table{border-collapse:collapse;width:100%}' +
    'th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:13px}' +
    '@media print{.no-print{display:none}}' +
    '</style></head><body>' + innerHTML + '</body></html>'
  );
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) { /* user can print manually */ } }, 300);
};

// Tying an imported row to a customer already on file. Without this a work order
// carries only a name, and every screen that reasons about a customer over time —
// last visit, when the next recurring visit is due, who is overdue — skips it,
// because those all group by customerId.
//
// The name alone is not enough. Several of this company's customers hold more
// than one account: "Sauld Juice #1" is three accounts at three addresses, "BEN
// NIKAR PROPERTIES NIKKAR" is four. In the jobs export, which carries no account
// number at all, the address is the only thing that tells them apart. Attaching
// all of a name's jobs to whichever account was indexed first would quietly file
// one property's service history under another — so where the name is shared,
// this matches on the address, and where it still cannot tell, it returns nothing
// rather than guessing.
const idKey = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Words that appear in so many addresses that they say nothing about which one
// this is, plus the state, which is the same for every customer here.
const ADDRESS_NOISE = new Set(['street', 'st', 'avenue', 'ave', 'av', 'boulevard',
  'blvd', 'road', 'rd', 'drive', 'dr', 'lane', 'ln', 'court', 'ct', 'place', 'pl',
  'way', 'terrace', 'circle', 'cir', 'highway', 'hwy', 'north', 'south', 'east',
  'west', 'n', 's', 'e', 'w', 'ca', 'california', 'usa', 'apt', 'unit', 'suite',
  'ste', 'no', 'thru', 'and', 'the', 'of']);

/**
 * The parts of an address worth comparing: the street number, every number in it,
 * and its real words. `street` is the leading number, which is the one part that
 * actually separates two of her sites — three Salud Juice accounts sit in the same
 * city and two of them share a postcode, so comparing numbers as a bag would call
 * them the same place.
 */
export const addressParts = (s) => {
  const tokens = String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const nums = tokens.filter(t => /^\d+$/.test(t));
  const words = new Set(tokens.filter(t => /[a-z]/.test(t) && !ADDRESS_NOISE.has(t)));
  return { street: nums[0] || '', numbers: new Set(nums), words };
};

/**
 * Whether two addresses are the same place. Deliberately strict about the street
 * number and loose about everything else: these two are one address —
 *   "1944 East 4th Street, Long Beach, CA, 90802"
 *   "1944 E 4TH ST, LONG BEACH"
 * while 1944 and 4918 in the same city are not, and that is the distinction that
 * decides whose service history a visit belongs to.
 */
export const sameAddress = (a, b) => {
  const x = addressParts(a);
  const y = addressParts(b);
  if (!x.street || !y.street) return false;
  if (x.street !== y.street) return false;
  if (!x.words.size || !y.words.size) return true;
  return [...x.words].some(w => y.words.has(w));
};

export const indexCustomers = (customers) => {
  const byAccount = new Map();
  const byName = new Map();
  for (const c of customers || []) {
    if (c?.accountNumber) byAccount.set(idKey(c.accountNumber), c);
    if (c?.name) {
      const k = idKey(c.name);
      if (!byName.has(k)) byName.set(k, []);
      // Every account sharing a name is kept, so a shared name can be resolved
      // by address instead of silently resolving to the first one seen.
      byName.get(k).push(c);
    }
  }
  return { byAccount, byName };
};

/** The customer a row belongs to, or null when the row cannot say which. */
export const matchCustomer = (index, { account, name, address } = {}) => {
  if (account) {
    const hit = index.byAccount.get(idKey(account));
    if (hit) return hit;
  }
  if (!name) return null;
  const candidates = index.byName.get(idKey(name)) || [];
  if (candidates.length === 1) return candidates[0];
  if (candidates.length === 0) return null;

  // A shared name. Only the address can settle it, and only if it settles it
  // outright: two candidates matching equally well is not an answer.
  if (!address) return null;
  const full = (c) => [c.address, c.city, c.state, c.zip].filter(Boolean).join(' ');
  const matches = candidates.filter(c => sameAddress(full(c), address));
  return matches.length === 1 ? matches[0] : null;
};

// A GorillaDesk export ends with a row like "Total 710 new jobs" — a count, not a
// record. It has no date, so it would otherwise be reported to the office as a row
// that was skipped for a missing date, which reads like lost data.
export const isSummaryRow = (row, idColumns) => {
  const cells = Object.values(row || {}).map(v => String(v ?? '').trim());
  const present = cells.filter(Boolean);
  const hasId = (idColumns || []).some(name => pickCell(row, name));
  if (hasId) return false;
  if (present.length > 3) return false;
  // The jobs export labels its total row ("Total 710 new jobs"). The invoice and
  // payment exports do not: they leave every cell blank but the amount, so the
  // row is a bare number and reads as a record with no date and no account.
  if (present.some(c => /^total\b/i.test(c))) return true;
  return present.length > 0 && present.every(c => /^\$?-?[\d,]+(\.\d+)?$/.test(c));
};

// GorillaDesk's invoice export has no service or address column. Both are inside
// the Job cell, the address in brackets:
//   "General Pest Control (361 THRU 365C W 9TH ST)"
// 622 of her 730 invoices are written that way; the rest read "n/a".
export const splitJobLabel = (text) => {
  const raw = String(text ?? '').trim();
  if (!raw || /^n\/a$/i.test(raw)) return { service: '', address: '' };
  const m = raw.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (!m) return { service: raw, address: '' };
  return { service: m[1].trim(), address: m[2].trim() };
};

// An invoice's Status says where the bill is, not what happened on site. Reading
// "Paid" as a job status would put a word the schedule has no meaning for on a
// visit, so a billing status is recognised and the visit is dated instead.
const BILLING_STATUSES = new Set(['draft', 'sent', 'paid', 'void', 'voided',
  'partial', 'partially paid', 'unpaid', 'overdue', 'past due', 'refunded']);
export const isBillingStatus = (v) => BILLING_STATUSES.has(String(v ?? '').trim().toLowerCase());

// A work order imported from a file that carries no ID of its own. GorillaDesk's
// job export has no ID column, so without a key derived from the row itself every
// re-import creates a second copy of every job. Same row in, same key out, so the
// second import recognises what is already on file.
export const importKey = (parts) => {
  const basis = parts
    .map(v => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim())
    .join('|');
  // djb2. Short, stable across sessions and browsers, and good enough to tell
  // one row of a service history from another — it is not a security hash.
  let h = 5381;
  for (let i = 0; i < basis.length; i++) h = ((h << 5) + h + basis.charCodeAt(i)) >>> 0;
  let h2 = 52711;
  for (let i = basis.length - 1; i >= 0; i--) h2 = ((h2 << 5) + h2 + basis.charCodeAt(i)) >>> 0;
  return `${h.toString(36)}${h2.toString(36)}`;
};

// GorillaDesk's own status words, mapped onto the vocabulary the scheduler uses.
// The spellings differ ("Canceled" against "Cancelled") and a status the app does
// not know falls through to grey and counts as open, so an unmapped "Completed"
// would leave 568 finished visits looking overdue.
const STATUS_ALIASES = {
  completed: 'Completed',
  complete: 'Completed',
  confirmed: 'Confirmed',
  unconfirmed: 'Pending Confirmation',
  pendingconfirmation: 'Pending Confirmation',
  reschedule: 'Reschedule',
  rescheduled: 'Reschedule',
  canceled: 'Cancelled',
  cancelled: 'Cancelled',
  terminateservice: 'Terminate Service',
  missed: 'Missed',
  enroute: 'En Route',
  inprogress: 'In Progress',
  workpool: 'Work Pool',
  scheduled: 'Scheduled',
};

/** A status from a file, in the scheduler's own words. Unknown values are kept. */
export const normStatus = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  return STATUS_ALIASES[raw.toLowerCase().replace(/[^a-z]/g, '')] || raw;
};
